import { ConcurrentMerkleTreeAccount } from "@solana/spl-account-compression";
import { useConnection } from "@solana/wallet-adapter-react";
import type { TransactionInstruction } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { isError, useMutation, useQueryClient } from "@tanstack/react-query";

import { createCompressedNftBurnIx } from "../../../actions/metaplex/compressed";
import { createMetaplexNftBurnIx } from "../../../actions/metaplex/nft";
import { ReactComponent as BurnIcon } from "../../../assets/flame.svg";
import { NFTChangeNotificationDisplay } from "../../../components/Notifications/TokenChangeNotificationDisplay";
import { useLanguageContext } from "../../../contexts/language/language";
import { useNFTContext } from "../../../contexts/nft";
import type { CompressedNFT } from "../../../models/nfts/nfts";
import { isCompressedNFT, isNFTWithTokenAccount, type NFTWithTokenAccount } from "../../../models/nfts/nfts";
import { getAssetProof } from "../../../utils/nft/readApi";
import { createRedeemableFeeInstruction } from "../../../utils/nft/utils";
import { notify } from "../../../utils/notifications";
import { handlePreSendTxnError, sendTransaction } from "../../../utils/transactions";
import { formatStringWithPlaceholders } from "../../../utils/utils";
import { useConnectedWallet } from "../../wallet";

export const useBurnMetaplexAsset = () => {
  const queryClient = useQueryClient();
  const wallet = useConnectedWallet();
  const { setUserNFTs } = useNFTContext();
  const { connection } = useConnection();
  const {
    currentLanguageData: {
      common: { notifications },
      components: { burnModal: burnModalText },
    },
  } = useLanguageContext();

  return useMutation({
    mutationFn: async ({ nft, amount }: { nft: NFTWithTokenAccount | CompressedNFT; amount: number }) => {
      if (!wallet) {
        return;
      }
      const instructions: TransactionInstruction[] = [];
      if (isNFTWithTokenAccount(nft)) {
        const burnIx = createMetaplexNftBurnIx(nft, amount, wallet.publicKey);
        const feeIx = await createRedeemableFeeInstruction(connection, wallet.publicKey, nft);
        instructions.push(burnIx, feeIx);
      }
      if (isCompressedNFT(nft)) {
        const [assetProof, treeAccount] = await Promise.all([
          getAssetProof(connection.rpcEndpoint, nft.address),
          ConcurrentMerkleTreeAccount.fromAccountAddress(connection, new PublicKey(nft.compression.tree)),
        ]);
        if (!isError(assetProof)) {
          const burnIx = createCompressedNftBurnIx(nft, treeAccount, assetProof, wallet.publicKey);
          instructions.push(burnIx);
        }
      }

      return await sendTransaction(connection, wallet, instructions, []);
    },
    onSuccess: (tx, { nft, amount }) => {
      notify({
        icon: <BurnIcon />,
        message: burnModalText.notifications.burnSuccessful,
        description: (
          <>
            <span>
              {formatStringWithPlaceholders(notifications.tokenBalanceChanges.youDid, notifications.youDidAction.burnt)}
            </span>
            <NFTChangeNotificationDisplay
              amount={amount}
              name={nft.name}
              url={nft.json?.image ?? ""}
              type={"decrease"}
            />
          </>
        ),
        type: "success",
        txId: tx,
      });
      setUserNFTs((currentState) => {
        const nftMap = new Map(currentState);
        nftMap.delete(nft.address.toBase58());
        return nftMap;
      });
      return queryClient.invalidateQueries({ queryKey: ["nftsWithTokensAndPrices", wallet!.publicKey.toBase58()] });
    },
    onError: (error: Error) => handlePreSendTxnError(error),
  });
};
